/**
 * Grid bootstrapper — finds [data-grid="..."] nodes, resolves the
 * library + config from the registry, and instantiates the table.
 *
 * Used by:
 *   - Lot-detail async tabs: call mountGridsIn(container) after HTML injection
 *   - Teardown on tab switch / BBL change: call destroyGridsIn(container)
 */
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator.min.css';
import './tabulator/theme.css';
// Side-effect import: attaches SheetJS to window.XLSX so Tabulator's
// built-in `table.download("xlsx", …)` can find it. See GH #277.
import './tabulator/_xlsx_shim.js';
import { registry } from './registry.js';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function errorMessage(err) {
  return err?.message || String(err || 'Unknown error');
}

function errorDetails(err) {
  return err?.stack || errorMessage(err);
}

function renderGridError(el, key, err, context = 'mount') {
  const message = errorMessage(err);
  const details = errorDetails(err);

  el.dataset.gridMountFailed = '1';
  el.dataset.gridMountError = message;
  el.innerHTML = `
    <div class="ps-grid-error" role="alert">
      <div class="ps-grid-error-title">Grid failed to load</div>
      <div class="ps-grid-error-message">
        The <code>${escapeHtml(key || 'unknown')}</code> table could not be rendered.
      </div>
      <details class="ps-grid-error-details" open>
        <summary>Technical details</summary>
        <pre>${escapeHtml(`[${context}] ${details}`)}</pre>
      </details>
    </div>
  `;
}

function preserveHorizontalScrollOnSort(el, table) {
  let scrollLeft = 0;

  const tableholder = () => el.querySelector('.tabulator-tableholder');
  const headerContents = () => el.querySelector('.tabulator-header-contents');

  const currentScrollLeft = () => tableholder()?.scrollLeft || headerContents()?.scrollLeft || 0;

  const saveScrollLeft = () => {
    scrollLeft = currentScrollLeft();
  };

  const restoreScrollLeft = () => {
    const holder = tableholder();
    const header = headerContents();
    if (holder) holder.scrollLeft = scrollLeft;
    if (header) header.scrollLeft = scrollLeft;
  };

  const restoreScrollLeftAfterRedraw = () => {
    restoreScrollLeft();
    requestAnimationFrame(() => {
      restoreScrollLeft();
      requestAnimationFrame(restoreScrollLeft);
    });
  };

  el.addEventListener('pointerdown', saveScrollLeft, true);
  el.addEventListener('click', saveScrollLeft, true);
  table.on('dataSorting', saveScrollLeft);
  table.on('dataSorted', restoreScrollLeftAfterRedraw);
  table.on('renderComplete', restoreScrollLeftAfterRedraw);
}

/**
 * Mount all unmounted grids inside `root`.
 * @param {HTMLElement|Document} root — scope to search within
 */
export async function mountGridsIn(root = document) {
  const nodes = root.querySelectorAll('[data-grid]:not([data-grid-mounted])');

  for (const el of nodes) {
    const key = el.dataset.grid;
    const entry = registry[key];

    if (!entry) {
      const err = new Error(`No grid config registered for "${key}"`);
      console.warn(`[grids] ${err.message}`);
      renderGridError(el, key, err, 'registry');
      continue;
    }

    try {
      const { buildOptions } = await entry.load();
      el.dataset.gridMounted = '1';
      delete el.dataset.gridMountFailed;
      delete el.dataset.gridMountError;

      if (entry.lib === 'tabulator') {
        const gridOptions = buildOptions(el);
        const { psAfterMount, ...tabulatorOptions } = gridOptions;
        const table = new Tabulator(el, {
          // PropertyScout default: prefer stable browser/app-shell scrolling
          // over Tabulator's virtual row renderer. Large datasets should use
          // server-side paging rather than client-side virtual DOM scrolling.
          renderVertical: 'basic',
          columnHeaderVertAlign: 'bottom',
          ...tabulatorOptions,
        });
        if (el.classList.contains('ps-grid-horizontal-scroll')) {
          preserveHorizontalScrollOnSort(el, table);
        }
        if (typeof psAfterMount === 'function') {
          psAfterMount(table, el);
        }
        el._grid = table;
      } else {
        throw new Error(`Unsupported grid library "${entry.lib}" for "${key}"`);
      }
      // Future: else if (entry.lib === 'ag_grid') { ... }
    } catch (err) {
      console.error(`[grids] failed to mount "${key}":`, err);
      renderGridError(el, key, err, 'mount');
    }
  }
}

/**
 * Destroy all mounted grids inside `root`, freeing resources.
 * @param {HTMLElement} root — scope to search within
 */
export function destroyGridsIn(root) {
  if (!root) return;
  root.querySelectorAll('[data-grid-mounted]').forEach((el) => {
    if (el._grid && typeof el._grid.destroy === 'function') {
      el._grid.destroy();
    }
    delete el._grid;
    delete el.dataset.gridMounted;
  });
}

// Expose globally so legacy jQuery code (load_part callbacks) can call it
window.PsGrids = { mountGridsIn, destroyGridsIn };
